// All marketing sections below the hero
import React from 'react';
import scopeworthLogoInverse from '../assets/scopeworth-logo-inverse.svg';
import { fillPricing } from './pricing.js';
import { getActiveLocale, localizedHref } from './i18n/bootstrap.js';
import { t } from './i18n/useT.js';
import { DEVELOPER_FOOTER } from './developer-footer.js';
import { trackDocs } from './developer-docs-analytics.js';

// Decorative right-side artwork for section headers. Each kind is an inline
// SVG sized 240x180 with no fill, designed to pair with .sw-section-head--with-art.
function SectionHeadArt({ kind }) {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const dim = { fill: 'none', stroke: 'currentColor', strokeWidth: 1, strokeLinecap: 'round', strokeLinejoin: 'round', opacity: 0.45 };
  const accent = { fill: 'none', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };

  if (kind === 'fix') {
    // Connected report — three nodes funneling into a single document
    return (
      <svg width="240" height="180" viewBox="0 0 240 180" aria-hidden="true">
        <circle cx="36" cy="42" r="14" {...stroke}/>
        <circle cx="36" cy="90" r="14" {...stroke}/>
        <circle cx="36" cy="138" r="14" {...stroke}/>
        <path d="M50 42 H110 Q130 42 130 62 V90 M50 90 H130 M50 138 H110 Q130 138 130 118 V90" {...dim}/>
        <rect x="140" y="46" width="76" height="92" rx="8" {...stroke}/>
        <path d="M154 70h48M154 86h48M154 102h36M154 118h28" {...dim}/>
        <path d="M188 132 l8 8 16-16" {...accent} stroke="#5EEAD4"/>
      </svg>
    );
  }

  if (kind === 'how') {
    // Workflow pipeline — four stages with connecting flow line
    return (
      <svg width="240" height="180" viewBox="0 0 240 180" aria-hidden="true">
        <path d="M30 90 H210" {...dim} strokeDasharray="3 5"/>
        {[30, 90, 150, 210].map((cx, i) => (
          <g key={cx}>
            <circle cx={cx} cy="90" r="18" {...stroke}/>
            <text x={cx} y="94" textAnchor="middle" fontSize="11" fontFamily="ui-monospace, monospace" fill="currentColor" opacity="0.85">{`0${i + 1}`}</text>
          </g>
        ))}
        <path d="M192 90 l12 -6 v12 z" fill="currentColor" opacity="0.7"/>
        <path d="M30 56 v-10 M90 56 v-10 M150 56 v-10 M210 56 v-10" {...dim}/>
        <rect x="22" y="22" width="16" height="14" rx="2" {...dim}/>
        <rect x="82" y="22" width="16" height="14" rx="2" {...dim}/>
        <rect x="142" y="22" width="16" height="14" rx="2" {...dim}/>
        <rect x="202" y="22" width="16" height="14" rx="2" {...dim}/>
      </svg>
    );
  }

  if (kind === 'inside') {
    // Report document with internal modules laid out as cards
    return (
      <svg width="240" height="180" viewBox="0 0 240 180" aria-hidden="true">
        <rect x="30" y="20" width="180" height="140" rx="10" {...stroke}/>
        <path d="M30 48 H210" {...dim}/>
        <circle cx="44" cy="34" r="2" fill="currentColor" opacity="0.4"/>
        <circle cx="54" cy="34" r="2" fill="currentColor" opacity="0.4"/>
        <circle cx="64" cy="34" r="2" fill="currentColor" opacity="0.4"/>
        <rect x="44" y="60" width="76" height="40" rx="6" {...stroke}/>
        <rect x="130" y="60" width="66" height="40" rx="6" {...dim}/>
        <rect x="44" y="110" width="60" height="36" rx="6" {...dim}/>
        <rect x="114" y="110" width="82" height="36" rx="6" {...stroke}/>
        <path d="M54 84 l12 -8 14 4 12 -10" stroke="#5EEAD4" {...accent}/>
        <circle cx="92" cy="70" r="2.2" fill="#5EEAD4"/>
      </svg>
    );
  }

  if (kind === 'benefits') {
    // Upward trending bars with arrow — value/ROI signal
    return (
      <svg width="240" height="180" viewBox="0 0 240 180" aria-hidden="true">
        <path d="M30 150 H210" {...dim}/>
        <path d="M30 150 V30" {...dim}/>
        {[
          { x: 54, h: 36 },
          { x: 90, h: 60 },
          { x: 126, h: 84 },
          { x: 162, h: 108 },
        ].map(({ x, h }) => (
          <rect key={x} x={x} y={150 - h} width="22" height={h} rx="3" {...stroke}/>
        ))}
        <path d="M48 132 L96 108 L138 78 L184 48" {...accent} stroke="#5EEAD4"/>
        <path d="M178 44 L188 44 L188 54" {...accent} stroke="#5EEAD4"/>
        <circle cx="184" cy="48" r="3" fill="#5EEAD4"/>
      </svg>
    );
  }

  return null;
}

// ---------- Problem ----------
export function ProblemSection() {
  const icons = [
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M9 9h6M9 13h4"/></svg>,
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h4l3-9 4 18 3-9h4"/></svg>,
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>,
  ];
  const cards = t('landing.problem.cards');
  return (
    <section className="sw-section sw-section-light" id="problem">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow">{t('landing.problem.eyebrow')}</div>
          <h2 className="sw-h2">{t('landing.problem.title')}</h2>
          <p className="sw-lead">{t('landing.problem.lead')}</p>
        </div>
        <div className="sw-grid-4">
          {cards.map((c, i) => (
            <a
              key={i}
              href={localizedHref('/product#problem-fix')}
              className="sw-pain-card sw-card-link"
            >
              <div className="sw-pain-card-h">
                <div className="sw-pain-icon">{icons[i]}</div>
                {c.stat && (
                  <div className="sw-pain-stat">
                    <span className="sw-pain-stat-v">{c.stat}</span>
                    <span className="sw-pain-stat-l">{c.statLabel}</span>
                  </div>
                )}
              </div>
              <h3 className="sw-pain-title">{c.title}</h3>
              <p className="sw-pain-body">{c.body}</p>
              <span className="sw-card-cta" aria-hidden="true">
                {t('landing.learnMore')}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Solution ----------
export function SolutionSection() {
  // No 01–04 here: these four are capabilities, not an order of operations.
  // Numbering them borrowed the grammar of the How-it-works steps further down,
  // where the sequence is the point — and weakened it by repetition.
  const meta = [
    { icon: 'box' },
    { icon: 'compass' },
    { icon: 'coin' },
    { icon: 'sparkle' },
  ];
  const blocks = t('landing.solution.blocks');
  return (
    <section className="sw-section sw-section-dark" id="solution">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head--with-art">
          <div className="sw-section-head-copy">
            <div className="sw-eyebrow sw-eyebrow-teal">{t('landing.solution.eyebrow')}</div>
            <h2 className="sw-h2 sw-h2-dark">{t('landing.solution.title')}</h2>
            <p className="sw-lead sw-lead-dark">
              {t('landing.solution.lead')}
            </p>
          </div>
          <div className="sw-section-head-art" aria-hidden="true">
            <SectionHeadArt kind="fix"/>
          </div>
        </div>
        <div className="sw-grid-4">
          {blocks.map((b, i) => (
            <a
              key={i}
              href={localizedHref('/product#modules')}
              className="sw-solution-card sw-card-link sw-card-link--dark"
            >
              <SolutionGlyph kind={meta[i].icon}/>
              <h3 className="sw-solution-title">{b.title}</h3>
              <p className="sw-solution-body">{b.body}</p>
              <span className="sw-card-cta sw-card-cta--dark" aria-hidden="true">
                {t('landing.learnMore')}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function SolutionGlyph({ kind }) {
  const stroke = { strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none', stroke: 'currentColor' };
  if (kind === 'box') return <svg width="28" height="28" viewBox="0 0 24 24" {...stroke}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12"/></svg>;
  // Euro, not dollar — every figure on this site is priced in $.
  if (kind === 'coin') return <svg width="28" height="28" viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9"/><path d="M15.2 9.4a4 4 0 1 0 0 5.2M8.4 11.1h6M8.4 13.1h5.4"/></svg>;
  if (kind === 'compass') return <svg width="28" height="28" viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9"/><path d="m16 8-2 6-6 2 2-6 6-2Z"/></svg>;
  if (kind === 'sparkle') return <svg width="28" height="28" viewBox="0 0 24 24" {...stroke}><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/><circle cx="12" cy="12" r="3"/></svg>;
  return null;
}

// ---------- How it works ----------
export function HowItWorksSection() {
  const meta = [
    { n: '01', icon: <PlugIcon/> },
    { n: '02', icon: <MapIcon/> },
    { n: '03', icon: <FileIcon/> },
    { n: '04', icon: <PresentIcon/> },
  ];
  const steps = t('landing.how.steps');
  return (
    <section className="sw-section sw-section-light" id="how">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head--with-art">
          <div className="sw-section-head-copy">
            <div className="sw-eyebrow">{t('landing.how.eyebrow')}</div>
            <h2 className="sw-h2">{t('landing.how.title')}</h2>
            <p className="sw-lead">{t('landing.how.lead')}</p>
          </div>
          <div className="sw-section-head-art" aria-hidden="true">
            <SectionHeadArt kind="how"/>
          </div>
        </div>
        <div className="sw-steps">
          {steps.map((s, i) => (
            <a
              key={i}
              href={localizedHref('/product#how')}
              className="sw-step sw-card-link"
            >
              <div className="sw-step-num">{meta[i].n}</div>
              <div className="sw-step-icon">{meta[i].icon}</div>
              <h3 className="sw-step-title">{s.t}</h3>
              <p className="sw-step-body">{s.b}</p>
              <span className="sw-card-cta" aria-hidden="true">
                {t('landing.learnMore')}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </span>
              {i < steps.length - 1 && <span className="sw-step-arrow" aria-hidden="true">
                <svg width="20" height="12" viewBox="0 0 20 12" fill="none"><path d="M0 6h18m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlugIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2v6M15 2v6M5 8h14v3a7 7 0 0 1-14 0V8ZM12 18v4"/></svg>; }
function MapIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 4-6 2v14l6-2 6 2 6-2V4l-6 2-6-2ZM9 4v14M15 6v14"/></svg>; }
function FileIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>; }
function PresentIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4h18v12H3zM12 16v5M8 21h8M7 10l3 3 3-4 4 4"/></svg>; }

// ---------- Early access / honesty ----------
// Dark access-pass card on a pale section — deliberately quiet: chip glow and
// a gentle float only, gated behind prefers-reduced-motion. The former VIP
// badge / star seal / shine sweep / pulse rings were removed as off-voice for
// the brand (numbers over adjectives). Styles live in styles.css under the
// ea- namespace; reveal-on-scroll is supplied by the parent .sw-reveal.
export function EarlyAccessSection() {
  return (
    <section className="sw-section sw-section-pale" id="beta" aria-labelledby="early-access-heading">
      <div className="sw-container">
        <div className="ea-card">
          {/* atmospheric accents */}
          <div className="ea-glow" aria-hidden="true" />
          <div className="ea-grid" aria-hidden="true" />

          {/* ---- left: copy + CTA ---- */}
          <div className="ea-content">
            <span className="ea-eyebrow">
              <span className="ea-dot" />
              {t('landing.earlyAccess.eyebrow')}
            </span>

            <h2 id="early-access-heading" className="ea-title">
              {t('landing.earlyAccess.title')}
            </h2>

            <p className="ea-sub">
              {t('landing.earlyAccess.sub')}
            </p>

            <div className="ea-actions">
              <button
                type="button"
                className="ea-btn-primary"
                onClick={() => window.openLeadModal('beta', 'early-access-section')}
              >
                {t('landing.earlyAccess.request')}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    className="ea-arrow"
                    d="M3 8h9M8.5 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                type="button"
                className="ea-btn-ghost"
                onClick={() => { window.location.href = localizedHref('/pricing'); }}
              >
                {t('landing.earlyAccess.download')}
              </button>
            </div>

            <p className="ea-trust">
              {t('landing.earlyAccess.trust')}
            </p>
          </div>

          {/* ---- right: access pass ---- */}
          <div className="ea-visual" aria-hidden="true">
            <div className="ea-pass">
              <svg viewBox="0 0 280 176" width="100%" height="100%" role="img">
                <defs>
                  <linearGradient id="ea-passFill" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" className="ea-stop-charcoal" />
                    <stop offset="100%" className="ea-stop-navy" />
                  </linearGradient>
                  <linearGradient id="ea-chipGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" className="ea-stop-blue" />
                    <stop offset="100%" className="ea-stop-teal" />
                  </linearGradient>
                  <clipPath id="ea-clip">
                    <rect x="2" y="2" width="276" height="172" rx="18" />
                  </clipPath>
                </defs>

                {/* card body */}
                <rect
                  x="2"
                  y="2"
                  width="276"
                  height="172"
                  rx="18"
                  fill="url(#ea-passFill)"
                  stroke="rgba(148,163,184,0.28)"
                  strokeWidth="1"
                />

                <g clipPath="url(#ea-clip)">
                  {/* faint accent wash */}
                  <circle className="ea-fill-blue" cx="250" cy="20" r="120" opacity="0.10" />

                  {/* chip */}
                  <rect className="ea-chip" x="26" y="30" width="46" height="34" rx="7" fill="url(#ea-chipGrad)" />
                  <line className="ea-stroke-navy" x1="26" y1="47" x2="72" y2="47" strokeWidth="1.4" opacity="0.55" />
                  <line className="ea-stroke-navy" x1="49" y1="30" x2="49" y2="64" strokeWidth="1.4" opacity="0.55" />

                  {/* period tag — factual, not a loyalty badge */}
                  <g transform="translate(188,30)">
                    <rect className="ea-stroke-teal" width="66" height="22" rx="11" fill="rgba(20,184,166,0.16)" strokeWidth="1" />
                    <text x="33" y="15" textAnchor="middle" fontSize="10" fontWeight="600" letterSpacing="1.2" fill="#5EEAD4">
                      2026
                    </text>
                  </g>

                  {/* label lines */}
                  <text className="ea-fill-slate" x="26" y="98" fontSize="10" letterSpacing="2">
                    FREE WORKSPACE
                  </text>
                  <rect x="26" y="112" width="150" height="9" rx="4" fill="rgba(203,213,225,0.55)" />
                  <rect x="26" y="130" width="96" height="8" rx="4" fill="rgba(203,213,225,0.30)" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- FAQ ----------
export function FaqSection() {
  const faqs = t('landing.faq.items');
  const [open, setOpen] = React.useState(0);
  return (
    <section className="sw-section sw-section-light" id="faq">
      <div className="sw-container">
        <div className="sw-faq-grid">
          <div className="sw-faq-aside">
            <div className="sw-eyebrow">{t('landing.faq.eyebrow')}</div>
            <h2 className="sw-h2">{t('landing.faq.title')}</h2>
            <p className="sw-lead">{t('landing.faq.leadPrefix')}<a href={localizedHref('/contact')} className="sw-textlink">{t('landing.faq.leadLink')}</a>{t('landing.faq.leadSuffix')}</p>
          </div>
          <div className="sw-faq-list">
            {faqs.map((f, i) => (
              <div key={i} className={`sw-faq-item ${open === i ? 'is-open' : ''}`}>
                <button className="sw-faq-q" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} aria-controls={`sw-faq-a-${i}`}>
                  <span>{f.q}</span>
                  <span className="sw-faq-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
                  </span>
                </button>
                <div className="sw-faq-a" id={`sw-faq-a-${i}`} hidden={open !== i}>{fillPricing(f.a, getActiveLocale())}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Final CTA ----------
export function FinalCtaSection() {
  return (
    <section className="sw-section sw-section-dark sw-section-final" id="final">
      <div className="sw-container">
        <div className="sw-final">
          <div className="sw-final-bg" aria-hidden="true">
            <svg viewBox="0 0 1200 320" preserveAspectRatio="none">
              <defs>
                <linearGradient id="finalLine" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#14B8A6" stopOpacity="0"/>
                  <stop offset="40%" stopColor="#14B8A6" stopOpacity="0.55"/>
                  <stop offset="100%" stopColor="#2563EB" stopOpacity="0"/>
                </linearGradient>
              </defs>
              <path d="M0 240 L150 220 L300 230 L450 200 L600 180 L750 150 L900 130 L1050 100 L1200 80" stroke="url(#finalLine)" strokeWidth="1.5" fill="none"/>
              <path d="M0 280 L150 270 L300 260 L450 240 L600 220 L750 200 L900 180 L1050 160 L1200 140" stroke="rgba(37,99,235,0.35)" strokeWidth="1" fill="none" strokeDasharray="4 4"/>
            </svg>
          </div>
          <div className="sw-eyebrow sw-eyebrow-teal">{t('finalCta.eyebrow')}</div>
          <h2 className="sw-h-display">{t('finalCta.title')}</h2>
          <p className="sw-lead sw-lead-dark">{t('finalCta.lead')}</p>
          <div className="sw-cta-actions">
            <button className="sw-btn sw-btn-primary sw-btn-lg" onClick={() => window.openSampleReport('home-final-cta')}>
              <span className="sw-btn-label">{t('finalCta.downloadSample')}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <a className="sw-btn sw-btn-ghost-dark sw-btn-lg" href={localizedHref('/contact?reason=demo')}><span className="sw-btn-label">{t('hero.bookDemo')}</span></a>
          </div>
          <p className="sw-final-note">
            <a href={localizedHref('/pricing')}>{t('finalCta.pricingLink')}</a>{t('finalCta.pricingRest')}
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------- Footer ----------
export function Footer({ onDeveloperResourcesClick } = {}) {
  const cols = [
    {
      h: t('footer.colProduct'),
      l: [
        { label: t('footer.linkProduct'), href: '/product' },
        { label: t('footer.linkPricing'), href: '/pricing' },
        { label: t('footer.linkIntegrations'), href: '/integrations' },
        { label: t('footer.linkUseCases'), href: '/use-cases' },
      ],
    },
    {
      h: t('footer.colCompany'),
      l: [
        { label: t('footer.linkAbout'), href: '/about' },
        { label: t('footer.linkContact'), href: '/contact' },
        { label: t('footer.linkBlog'), href: '/blog' },
      ],
    },
    { h: DEVELOPER_FOOTER.title, l: [{ label: DEVELOPER_FOOTER.label, href: DEVELOPER_FOOTER.href, developerResources: true }] },
    {
      h: t('footer.colTrust'),
      l: [
        { label: t('footer.linkSecurity'), href: '/security' },
        { label: t('footer.linkPrivacy'), href: '/privacy' },
        { label: t('footer.linkTerms'), href: '/terms' },
          { label: t('footer.linkSubprocessors'), href: '/subprocessors' },
          { label: t('footer.linkDpa'), href: '/dpa' },
          { label: t('footer.linkPrivacyOptOut'), href: '/privacy#advertising-optout' },
        // Reopens the consent bar (scripts/CookieConsent.jsx) instead of navigating,
        // so a choice can be changed from any page.
        { label: t('footer.linkCookies'), action: 'cookies' },
      ],
    },
  ];
  const year = new Date().getFullYear();
  return (
    <footer className="sw-footer">
      <div className="sw-container">
        <div className="sw-footer-grid">
          <div>
            <a href={localizedHref('/')} aria-label={t('nav.home')} style={{ display: 'inline-block' }}>
              <img src={scopeworthLogoInverse} alt="ScopeWorth" height="28"/>
            </a>
            <p className="sw-footer-tagline">{t('footer.tagline')}</p>
            <div className="sw-footer-cta">
              <button
                className="sw-btn sw-btn-primary sw-btn-sm"
                onClick={() => window.openSampleReport && window.openSampleReport('footer')}
              >
                <span className="sw-btn-label">{t('footer.downloadSample')}</span>
              </button>
            </div>
          </div>
          {cols.map(c => (
            <div key={c.h}>
              <div className="sw-footer-col-h">{c.h}</div>
              <ul className="sw-footer-col-l">
                {c.l.map(item => (
                  <li key={item.href || item.action}>
                    {item.href ? (
                      <a href={item.developerResources ? DEVELOPER_FOOTER.href : localizedHref(item.href)} target={item.developerResources ? '_blank' : undefined} rel={item.developerResources ? 'noopener noreferrer' : undefined} data-docs-event={item.developerResources ? DEVELOPER_FOOTER.event : undefined} data-docs-location={item.developerResources ? DEVELOPER_FOOTER.location : undefined} onClick={item.developerResources ? () => { try { trackDocs(DEVELOPER_FOOTER.event, { location: DEVELOPER_FOOTER.location }, true); onDeveloperResourcesClick?.({ location: DEVELOPER_FOOTER.location }); } catch {} } : undefined}>{item.label}</a>
                    ) : (
                      <button type="button" onClick={() => window.openCookiePreferences?.()}>
                        {item.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="sw-footer-bar">
          <span>{t('footer.rights', { year })}</span>
          <div className="sw-footer-bar-r">
            <span className="sw-dot sw-dot-teal"></span>
            <span>{t('footer.partners')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Guarded: the build-time prerender imports this module in Node, where
// `window` is absent. Same pattern as Forms.jsx and CookieConsent.jsx.
if (typeof window !== 'undefined') Object.assign(window, {
  ProblemSection, SolutionSection, HowItWorksSection, EarlyAccessSection,
  FaqSection, FinalCtaSection, Footer,
});
